import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import type { Locale } from '@xdocs/i18n';
import { safeT } from '../i18n/safeT.js';
import { AuthTransportError } from './authTransport.js';

/**
 * Applies a server-rejected `err` envelope to an RHF form (CODE_GUIDELINES
 * §5.4/§16: `VALIDATION` alerts carry per-field catalog-key messages —
 * round-trip parity with the SAME shared zod schema client-side validated
 * against). Non-field alerts (`UNAUTHENTICATED`/`CONFLICT`/`RATE_LIMITED`/…)
 * have no field to attach to — the caller renders `bannerMessage` inline
 * atop the form (ux.md: page-level error atop the affected region).
 *
 * Returns the localized banner message, or `null` when the alert was fully
 * absorbed into field errors.
 */
export function applyServerAlert<TFieldValues extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<TFieldValues>,
  locale: Locale,
): string | null {
  if (err instanceof AuthTransportError) {
    const alert = err.alert;
    if (alert.code === 'VALIDATION' && alert.fields && alert.fields.length > 0) {
      for (const field of alert.fields) {
        setError(field.path as Path<TFieldValues>, { type: 'server', message: safeT(locale, field.message) });
      }
      return null;
    }
    return safeT(locale, alert.message);
  }
  return safeT(locale, 'errors:code.INTERNAL');
}
